import React, { useEffect, useState } from "react";
import Select from "react-select";

import User from "../../cards/User";
import useGetAllUserBids from "../../../hooks/useGetAllUserBids";
import { ClipLoader } from "react-spinners";
import useGetAllLocalBids from "../../../hooks/useGetAllLocalCarsBids";
import { customStyles } from "./dropdownStyle";

const AllBids = () => {
  const { bids, loading, error, fetchBids } = useGetAllUserBids();
  const { localBids, loading: localLoading, error: localError, fetchLocalBids } = useGetAllLocalBids();

  const [selectedOption, setSelectedOption] = useState("bidding");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOption, setFilterOption] = useState({ value: "all", label: "All Bids" });
  const [sortOption, setSortOption] = useState({ value: "newest", label: "Sort by: Newest" });

  // Filter & Sort Options
  const filterOptions = [
    { value: "all", label: "All Bids" },
    { value: "active", label: "Winning Bids" },
    { value: "expired", label: "Outbidded Bids" }
  ];

  const sortOptions = [
    { value: "newest", label: "Sort by: Newest" },
    { value: "oldest", label: "Sort by: Oldest" }
  ];

  // Fetch data when selectedOption changes
  useEffect(() => {
    if (selectedOption === "bidding") {
      fetchBids();
    } else {
      fetchLocalBids();
    }
  }, [selectedOption]);

  // Select appropriate data, loading, and error based on selectedOption
  const isLoading = selectedOption === "bidding" ? loading : localLoading;
  const hasError = selectedOption === "bidding" ? error : localError;
  const data = selectedOption === "bidding" ? bids?.data : localBids?.data;

  // Apply filtering, search, and sorting
  const filteredAndSortedData = React.useMemo(() => {
    if (!data) return [];

    let result = data.filter(bid =>
      bid?.carDetails?.title?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (filterOption.value === "active") {
      result = result.filter(bid => bid.isValid);
    } else if (filterOption.value === "expired") {
      result = result.filter(bid => !bid.isValid);
    }

    return result.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);

      return sortOption.value === "newest" ? dateB - dateA : dateA - dateB;
    });
  }, [data, searchTerm, filterOption.value, sortOption.value]);

  return (
    <div className="w-[90%] md:w-[650px] lg:w-[85vw] mx-auto mt-6 lg:mt-[1.5vw] text-black">
      
      {/* Header and Tabs */}
      <div className="flex justify-between items-center">
        <h1 className="text-[24px] md:text-[36px] lg:text-[2.3vw] font-urbanist font-bold">My Bids</h1>

        <div className="flex justify-end lg:justify-start space-x-4 mb-3 lg:mb-[1.5vw]">
          <button
            className={`px-[1vw] py-[0.5vw] lg:text-[1vw] font-semibold rounded-md ${
              selectedOption === "bidding" ? "bg-primary-red text-white" : "bg-gray-200 text-gray-600"
            }`}
            onClick={() => setSelectedOption("bidding")}
          >
            Bidding Cars
          </button>
          <button
            className={`px-[1vw] py-[0.5vw] lg:text-[1vw] font-semibold rounded-md ${
              selectedOption === "local" ? "bg-primary-red text-white" : "bg-gray-200 text-gray-600"
            }`}
            onClick={() => setSelectedOption("local")}
          >
            Local Cars
          </button>
        </div>
      </div>

      {/* Search and Dropdown Filters */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
        <div className="w-full md:w-1/3">
          <input
            type="text"
            placeholder="Search by title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={isLoading} // Disable while loading
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-red disabled:opacity-50"
          />
        </div>

        <div className="flex space-x-3 w-full md:w-auto">
          <div className="w-40">
            <Select
              value={filterOption}
              onChange={setFilterOption}
              options={filterOptions}
              styles={customStyles}
              isSearchable={false}
              isDisabled={isLoading} // isable while loading
            />
          </div>

          <div className="w-48">
            <Select
              value={sortOption}
              onChange={setSortOption}
              options={sortOptions}
              styles={customStyles}
              isSearchable={false}
              isDisabled={isLoading} // Disable while loading
            />
          </div>
        </div>
      </div>

      {/* MAIN CONDITIONAL BLOCK — only one will show at a time */}
      {isLoading ? (
        // Show loader when data is loading
        <div className="flex justify-center items-center mb-4 min-h-[50vh]">
          <ClipLoader size={30} color={"#D0021B"} loading={true} />
        </div>
      ) : hasError ? (
        // Show error only after loading finishes
        <p className="text-center text-red-500 mb-4 min-h-[50vh]">Error: {hasError}</p>
      ) : filteredAndSortedData.length === 0 ? (
        // Show 'No Bids' message when data is empty
        <div className="flex flex-col items-center justify-center min-h-[50vh]">
          <p className="text-2xl font-bold text-gray-500">
            {searchTerm ? "No matching bids found" : "No bids available for any car"}
          </p>
          <p className="text-gray-400 mt-2 min-h-[50vh]">
            {searchTerm ? "Try adjusting your search or filters." : "It looks like you haven't placed any bids yet."}
          </p>
        </div>
      ) : (

        // Show table when data is available
        <div className="w-full lg:w-[85vw] mx-auto overflow-x-auto">
          <table className="min-w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-4 py-2 text-sm font-medium text-gray-700">Car Image</th>
                <th className="px-4 py-2 text-sm font-medium text-gray-700">Title</th>
                <th className="px-4 py-2 text-sm font-medium text-gray-700">VIN</th>
                <th className="px-4 py-2 text-sm font-medium text-gray-700">No Of Bids</th>
                <th className="px-4 py-2 text-sm font-medium text-gray-700">Bid Price</th>
                <th className="px-4 py-2 text-sm font-medium text-gray-700">Time Left</th>
                <th className="px-4 py-2 text-sm font-medium text-gray-700">Current Bid</th>
                <th className="px-4 py-2 text-sm font-medium text-gray-700">Bid On</th>
                <th className="px-4 py-2 text-sm font-medium text-gray-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedData.map((bid) => (
                <User selectedOption={selectedOption} key={bid?.id} bid={bid} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AllBids;